import React from 'react';
import {
  ALLERGEN_HIGHLIGHT_REGEX,
  BRANCH_MAPPING,
  BRANCH_STOCK_LABELS,
  BRANCH_STOCK_STATUS,
  STOCK_CHECK,
} from '../constants/constants';
import {PriceUnavailable} from '../components/Product/Product.style';
import SentryHelper from './sentry.helper';

export const syncBasketQuantityWithProducts = (basketHashMap, items) => {
  let converted = items;
  if (basketHashMap && items) {
    converted = items.map(product =>
      syncBasketQuantityWithOneProduct(basketHashMap, product)
    );
  }
  return converted;
};

export const syncBasketQuantityWithOneProduct = (basketHashMap, item) => {
  if (basketHashMap && item && item.itemId) {
    const hashMapItem = basketHashMap[item.itemId];
    if (!hashMapItem) {
      return item;
    }
    const basketQuantity =
      hashMapItem &&
      hashMapItem.stockCheck &&
      hashMapItem.stockCheck.result &&
      (hashMapItem.stockCheck.result === STOCK_CHECK.OUT_OF_STOCK ||
        hashMapItem.stockCheck.result === STOCK_CHECK.DISCONTINUED ||
        hashMapItem.stockCheck.result === STOCK_CHECK.NOT_ALLOWED)
        ? hashMapItem.stockCheck.quantityAsked
        : hashMapItem.quantity;
    if (!basketQuantity) {
      SentryHelper.debugQuantityZero({
        message: `product.helper.js syncBasketQuantityWithOneProduct - basketQuantity: ${basketQuantity} (${typeof basketQuantity}),`,
      });
    }
    return hashMapItem && hashMapItem.quantity > -1
      ? Object.assign({}, item, {
          basketQuantity: basketQuantity,
          basketItemUuid: hashMapItem.basketItemUuid,
        })
      : item;
  }
  return item;
};

/**
 * Interleaves the peer-gap trial cards into the search results for rendering.
 *
 * Returns render descriptors rather than bare products so the caller knows which entries are
 * featured. The underlying results array is left alone — it still drives infinite-scroll paging,
 * and inflating its length would make the scroller ask for pages that do not exist.
 *
 * `searchPosition` is kept as the item's index within the real results (and, for featured cards,
 * the position the backend assigned) so click and add events line up with the search impression
 * logged server-side.
 *
 * A featured entry's `position` is an index into the real results, not into the rendered grid:
 * position 9 means "after the 9th product", regardless of how many featured cards precede it.
 */
export const spliceFeaturedIntoResults = (results, featured) => {
  const items = (results || []).map((product, index) => ({
    product,
    searchPosition: index,
    isFeatured: false,
  }));
  // The backend can only de-duplicate against page 0, but infinite scroll keeps appending organic
  // results. Re-checking the whole accumulated list here stops a featured product appearing twice
  // once the customer scrolls far enough to reach its organic rank.
  const organicItemIds = new Set(
    (results || []).map(product => product && product.itemId).filter(Boolean)
  );
  (featured || [])
    .filter(
      entry =>
        entry && entry.product && !organicItemIds.has(entry.product.itemId)
    )
    // Ascending, because each insert shifts everything after it: applying them out of order would
    // land the later cards in the wrong slots.
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .forEach((entry, inserted) => {
      // `position` counts real search results, matching what the backend logged, so each card
      // already inserted has to be added back on. Without the offset a card configured for
      // "after the 9th result" would land after the 8th.
      const position = Math.min(
        Math.max(entry.position || 0, 0) + inserted,
        items.length
      );
      items.splice(position, 0, {
        product: entry.product,
        searchPosition: entry.position,
        isFeatured: true,
        reason: entry.reason,
        campaignId: entry.campaignId,
        title: entry.title,
        intro: entry.intro,
      });
    });
  return items;
};

/**
 * Drops featured cards for products the customer already has in their basket.
 *
 * Meant to be applied once, as a search's results arrive, and deliberately NOT while rendering:
 * the render reads the live basket, so filtering there would make the card vanish the instant its
 * Add button was pressed — pulling the grid out from under the customer mid-click, and taking with
 * it the quantity stepper they were about to use. Filtering at the response keeps the card for the
 * rest of that search and drops it from the next one.
 *
 * Entries without a product are dropped too: they cannot be rendered, and letting them through
 * only offsets the positions of the cards that can be.
 */
export const excludeFeaturedAlreadyInBasket = (featured, basketHashMap) =>
  (featured || []).filter(entry => {
    if (!entry || !entry.product) {
      return false;
    }
    const basketItem = basketHashMap && basketHashMap[entry.product.itemId];
    return !(basketItem && basketItem.quantity > 0);
  });

export const getLogistics = product => {
  return [
    ['origin', product.origin],
    ['storage', product.storageCondition],
    ['net weight', product.itemNetWeight + 'kg'],
    ['collection unit price', getCollectionUnitPrice(product)],
    [
      'delivery unit price',
      (!!product.delivery && product.delivery.unitPriceDisplay) || '',
    ],
    ['size or cut', product.sizeOrCut],
    ['quantity per layer', product.qtyPerLayer],
    ['pallet quantity', product.standardPalletQty],
    [
      'dimension',
      product.depth + ' x ' + product.height + ' x ' + product.width + 'cm',
    ],
  ];
};

// Label/value pairs in the same shape as getLogistics, so they render through the
// existing Storage & size markup. Stock is read from the product's branches list —
// the branch codes that actually hold the item — rather than the product-level
// isAvailable flag, which is only branch-scoped when the branch query succeeded.
export const getBranchAvailabilityRows = (product, branchId, branchDetails) => {
  if (!product || !branchId) {
    return [];
  }
  const branchName =
    (branchDetails && branchDetails.branchName) || BRANCH_MAPPING[branchId];
  if (!branchName) {
    return [];
  }
  const inStock = (product.branches || []).some(
    branch => branch.locationId === branchId
  );
  const rows = [
    [BRANCH_STOCK_LABELS.BRANCH, branchName],
    [
      BRANCH_STOCK_LABELS.AVAILABILITY,
      inStock ? BRANCH_STOCK_STATUS.IN_STOCK : BRANCH_STOCK_STATUS.OUT_OF_STOCK,
    ],
  ];
  const address = branchDetails && branchDetails.address;
  if (address && address.address) {
    // Some branch records already end a line with a comma, so collapse the repeats
    // the newline substitution would otherwise produce ("7 Solar Way,, Innova Park").
    const street = address.address
      .replace(/(\r\n|\n|\r)/gm, ', ')
      .replace(/\s*,(\s*,)+/g, ',')
      .replace(/\s+/g, ' ')
      .trim();
    rows.push([
      BRANCH_STOCK_LABELS.ADDRESS,
      address.zipcode ? `${street}, ${address.zipcode}` : street,
    ]);
  }
  return rows;
};

export const getNutrition = product => {
  return [
    ['Energy (KJ)', product.energyKJ],
    ['Energy (KCAL)', product.energyKCAL],
    ['Fat (g)', product.fatG],
    ['- of which Saturates (g)', product.fatSaturatesG],
    ['Carbohydrate (g)', product.carbohydrateG],
    ['- of which Sugars (g)', product.carbohydrateSugarsG],
    ['Protein (g)', product.proteinG],
    ['Salt (g)', product.sodiumG],
  ];
};

export const getPriceUnavailableText = alignLeft => (
  <PriceUnavailable $left={alignLeft}>Price unavailable</PriceUnavailable>
);

export const getBrandUrl = (branchName, brand) => {
  if (!branchName) {
    return null;
  }
  return `/brand/${branchName}/${brand}`;
};

export const generateProductInfo = (item, isCollection) => {
  if (!item.quantity) {
    SentryHelper.debugQuantityZero({
      message: `product.helper.js generateProductInfo - item.quantity: ${
        item.quantity
      } (${typeof item.quantity}),`,
    });
  }
  return {
    mealUuid: item.uuid,
    itemId: item.itemId,
    quantity: item.quantity,
    step: item.step,
    unitPrice: isCollection ? item.collectionPrice : item.deliveryPrice,
  };
};

export const generateProductArrayInfo = (items, isCollection) =>
  items.map(item => {
    return generateProductInfo(item, isCollection);
  });

export const getProductStep = (product, isDelivery) => {
  if (!product) {
    return 1;
  }
  return (
    product.quantityStep ||
    (isDelivery
      ? (product.delivery && product.delivery.step) || 1
      : (product.collection && product.collection.step) || 1)
  );
};

export const getProductMax = (product, isDelivery) => {
  if (!product) {
    return 0;
  }
  return (
    product.quantityMax ||
    (isDelivery
      ? (product.delivery && product.delivery.max) || 0
      : (product.collection && product.collection.max) || 0)
  );
};

export const boldenIngredientAllergens = ingredientsText => {
  if (!ingredientsText) {
    return ingredientsText;
  }
  const segments = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  ALLERGEN_HIGHLIGHT_REGEX.lastIndex = 0;
  while ((match = ALLERGEN_HIGHLIGHT_REGEX.exec(ingredientsText))) {
    if (match.index > lastIndex) {
      segments.push(ingredientsText.slice(lastIndex, match.index));
    }
    segments.push(<b key={key++}>{match[0]}</b>);
    lastIndex = match.index + match[0].length;
  }
  segments.push(ingredientsText.slice(lastIndex));
  return segments;
};

export const getCollectionUnitPrice = product => {
  const cheapPriceIndex =
    product &&
    product.collectionMultiBuy &&
    product.collectionMultiBuy.length - 1;
  return (
    (!!product.collection &&
      ((product.collectionMultiBuy &&
        product.collectionMultiBuy[cheapPriceIndex] &&
        product.collectionMultiBuy[cheapPriceIndex].unitPriceDisplay) ||
        product.collection.unitPriceDisplay)) ||
    ''
  );
};
